import moment from 'moment';
import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { rtaNames } from '../components/common/FatcaCheck.types';

type sipHookProps = {
    stpFlag?: boolean;
    frequencyType: string;
    kfinFlag: boolean;
    numInstallments: number;
    sipDay: string;
    sipTat: number;
    setErrors?: Dispatch<any>;
    perpetual?: boolean;
};

enum WeekDays {
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
}

export enum freqType {
    W = 'Weekly',
    M = 'Monthly',
    D = 'Daily',
    DZ = 'DailyZip',
    Q = 'Quarterly',
    H = 'Halfyearly',
    A = 'Annual',
    F = 'Fortnightly',
}

export function monthDiff(dateFrom: Date, dateTo: Date) {
    const newDateFrom = moment(dateFrom, 'MM/DD/YYYY');
    const newDateTo = moment(dateTo, 'MM/DD/YYYY');
    return newDateTo.diff(newDateFrom, 'months');
}

function weekDiff(dateFrom: Date, dateTo: Date) {
    const newDateFrom = moment(dateFrom, 'MM/DD/YYYY');
    const newDateTo = moment(dateTo, 'MM/DD/YYYY');
    return newDateTo.diff(newDateFrom, 'weeks');
}
function daysIntoYear(date: Date) {
    return (
        (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
            Date.UTC(date.getFullYear(), 0, 0)) /
        24 /
        60 /
        60 /
        1000
    );
}

function daysInMonth(month: any, year: any) {
    return new Date(year, month, 0).getDate();
}

enum Day {
    Sunday,
    Monday,
    Tuesday,
    Wednesday,
    Thursday,
    Friday,
    Saturday,
}

function getDaysToAdd(currentDay: Day) {
    if (currentDay === Day.Sunday) return 1;
    if (currentDay === Day.Saturday) return 2;
}

export function isWeekendDay(currentDay: Day) {
    return currentDay === Day.Saturday || currentDay === Day.Sunday;
}

const useSip = ({
    frequencyType,
    numInstallments,
    sipDay,
    sipTat,
    setErrors,
    stpFlag = false,
    perpetual,
    kfinFlag,
}: sipHookProps): [
        Date,
        Date,
        Dispatch<SetStateAction<Date>>,
        Dispatch<SetStateAction<Date>>,
        any,
        number,
    ] => {
    const [startDate, setStartDate] = React.useState<Date>(new Date());
    const [endDate, setEndDate] = React.useState<Date>(new Date());

    const handleError = (value: boolean) => {
        setErrors &&
            setErrors((prevErrors: any) => {
                return {
                    ...prevErrors,
                    dateError: value,
                };
            });
    };
    let installment_diff = 0,
        monthMultiplier = 1,
        dayMultiplier = 1;
    switch (frequencyType.replace(/[^a-zA-Z]/g, '')) {
        case freqType.M:
            installment_diff = monthDiff(startDate, endDate);
            break;
        case freqType.H:
            installment_diff = monthDiff(startDate, endDate) / 6.0;
            monthMultiplier = 6;
            break;
        case freqType.Q:
            installment_diff = monthDiff(startDate, endDate) / 3.0;
            monthMultiplier = 3;
            break;
        case freqType.W:
            installment_diff = weekDiff(startDate, endDate);
            dayMultiplier = 7;
            break;
        case freqType.F:
            installment_diff = weekDiff(startDate, endDate) / 2.0;
            dayMultiplier = 14;
            break;
        case freqType.A:
            installment_diff = monthDiff(startDate, endDate) / 12.0;
            monthMultiplier = 12;
            break;
        case freqType.D:
        case freqType.DZ:
            let dft = endDate.getTime() - startDate.getTime();
            installment_diff = dft / (1000 * 3600 * 24);
            break;
        default:
            throw new Error(`${frequencyType} is not supported`);
    }
    useEffect(() => {
        if (!!sipDay && !!sipTat) {
            let todayDate = new Date();
            // date after which the sip must happen
            let sipTatDate = new Date(todayDate.setDate(todayDate.getDate() + sipTat));
            // currently selected date
            let selectedSipDate = new Date(todayDate.setDate(parseInt(sipDay)));
            let newStartDate;
            if (selectedSipDate >= sipTatDate) {
                // if sip date is after the tat date
                newStartDate = selectedSipDate;
            } else {
                // if sip date is before the tat date
                selectedSipDate.setMonth(sipTatDate.getMonth() + 1);
                newStartDate = selectedSipDate;
            }
            if (!stpFlag && [freqType.D, freqType.DZ].includes(frequencyType as freqType)) {
                const startDateDayOfWeek = newStartDate.getDay();
                setStartDate(
                    moment(newStartDate, 'MM/DD/YYYY')
                        .add(getDaysToAdd(startDateDayOfWeek), 'days')
                        .toDate(),
                );
                return;
            }
            setStartDate(newStartDate);
        }
    }, [sipDay, sipTat, frequencyType]);

    const resetEndDate = () => {
        if (!stpFlag && [freqType.D].includes(frequencyType as freqType) && kfinFlag) {
            const newEndDate = moment(startDate, 'MM/DD/YYYY')
                .add((numInstallments - 1) * dayMultiplier, 'days')
                .toDate();
            // check for saturday and sunday
            const endDateDayofWeek = newEndDate.getDay();
            setEndDate(
                moment(newEndDate, 'MM/DD/YYYY')
                    .add(getDaysToAdd(endDateDayofWeek), 'days')
                    .toDate(),
            );
            handleError(false);
            return;
        }
        if (
            frequencyType == freqType.W ||
            frequencyType == freqType.D ||
            frequencyType == freqType.DZ ||
            frequencyType === freqType.F
        ) {
            setEndDate(
                moment(startDate, 'MM/DD/YYYY')
                    .add((numInstallments - 1) * dayMultiplier, 'days')
                    .toDate(),
            );
            handleError(false);
            return;
        }
        setEndDate(
            moment(startDate, 'MM/DD/YYYY')
                .add((numInstallments - 1) * monthMultiplier, 'months')
                .toDate(),
        );
        handleError(false);
    };
    useEffect(() => {
        if (!perpetual) {
            resetEndDate();
        }
    }, [startDate, frequencyType, sipDay, numInstallments]);
    useEffect(() => {
        parseInt(installment_diff.toString()) < numInstallments - 1
            ? handleError(true)
            : handleError(false);
    }, [endDate]);
    return [
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        resetEndDate,
        Math.ceil(installment_diff),
    ];
};
export default useSip;
